import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {
  Ticket,
  TicketAggregate,
  TicketRecordType,
} from 'src/app/models/ticket/ticket.model';
import { getTicketType } from 'src/app/models/ticket/ticket.util';

@Component({
  selector: 'app-ticket-count-chart',
  templateUrl: './ticket-count-chart.component.html',
  standalone: false,
})
export class TicketCountChartComponent implements OnChanges, OnInit {
  @Output() timeRangeChanged = new EventEmitter();

  @Input() tickets: Ticket[];

  @Input() isLoading: boolean;

  @Input() ticketAggregate: TicketAggregate | null = null;

  public ticketTypeCount: any;

  public averageTimeToFirstTouch: string;

  public averageTimeToRepair: string;

  filteredTickets: Ticket[];

  selectedTimeRange: string = '';

  proactiveTickets: number;

  nonProactiveTickets: number;

  nonProactiveAverageTimeToRepair: string;

  proactiveAverageTimeToRepair: string;

  nonProactiveAverageTimeToFirstTouch: string;

  proactiveAverageTimeToFirstTouch: string;

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    if (this.tickets?.length) {
      this.updateTicketCounts();
    }
    this.filteredTickets = this.tickets;
  }

  ngOnChanges(): void {
    this.filteredTickets = this.tickets;
    this.updateTicketCounts();
  }

  onTimeRangeChange(event: any): void {
    this.selectedTimeRange = event.value;
    this.updateTicketCounts();

    this.timeRangeChanged.emit(event.value);
  }

  updateTicketCounts(): void {
    this.isLoading = true;
    this.ticketTypeCount = this.ticketAggregate;
    const proactiveTickets = this.filteredTickets.filter(
      (ticket) => getTicketType(ticket)[0] === TicketRecordType.Proactive,
    );
    const nonProactiveTickets = this.filteredTickets.filter((ticket) => {
      const ticketType = getTicketType(ticket)[0];
      return ticketType !== TicketRecordType.Proactive && ticketType != null;
    });

    const proactiveAverageTimeToFirstTouch =
      this.calculateAverageTimeToFirstTouch(proactiveTickets);
    this.proactiveAverageTimeToFirstTouch =
      proactiveAverageTimeToFirstTouch !== null
        ? this.convertMillisecondsToDaysAndHours(
            proactiveAverageTimeToFirstTouch,
          )
        : 'N/A';

    const nonProactiveAverageTimeToFirstTouch =
      this.calculateAverageTimeToFirstTouch(nonProactiveTickets);
    this.nonProactiveAverageTimeToFirstTouch =
      nonProactiveAverageTimeToFirstTouch !== null
        ? this.convertMillisecondsToDaysAndHours(
            nonProactiveAverageTimeToFirstTouch,
          )
        : 'N/A';

    const proactiveAverageTimeToRepair =
      this.calculateAverageTimeToRepair(proactiveTickets);
    this.proactiveAverageTimeToRepair = proactiveAverageTimeToRepair;
    const nonProactiveAverageTimeToRepair =
      this.calculateAverageTimeToRepair(nonProactiveTickets);
    this.nonProactiveAverageTimeToRepair = nonProactiveAverageTimeToRepair;
    this.isLoading = false;
    this.cdr.detectChanges();
  }

  calculateAverageTimeToRepair(tickets: Ticket[]): string {
    let totalTime = 0;
    let count = 0;

    tickets.forEach((ticket) => {
      const type = getTicketType(ticket);
      if (
        type !== null &&
        type !== undefined &&
        ticket?.Time_to_Repair_Days__c !== null &&
        ticket?.Time_to_Repair_Days__c !== undefined
      ) {
        totalTime += ticket.Time_to_Repair_Days__c;
        count += 1;
      }
    });

    if (count === 0) {
      return 'N/A';
    }

    const averageTime = totalTime / count;
    return this.convertDaysToDaysAndHours(averageTime);
  }

  convertDaysToDaysAndHours(days: number): string {
    const wholeDays = Math.floor(days);
    const remainingHours = (days - wholeDays) * 24;
    const hours = Math.floor(remainingHours);
    const minutes = Math.floor((remainingHours - hours) * 60);

    if (wholeDays === 0) {
      return `${hours} hr, ${minutes} min`;
    }
    if (hours === 0) {
      return `${wholeDays} days`;
    }
    return `${wholeDays} days, ${hours} hr, ${minutes} min`;
  }

  convertMillisecondsToDaysAndHours(milliseconds: number): string {
    const days = Math.floor(milliseconds / (1000 * 60 * 60 * 24));
    const hours = Math.floor(
      (milliseconds % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
    );
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));
    return `${days} days, ${hours} hr, ${minutes} min`;
  }

  calculateAverageTimeToFirstTouch(tickets: Ticket[]): number | null {
    let totalTime = 0;
    let count = 0;

    tickets.forEach((ticket) => {
      if (ticket.First_touch__c !== null) {
        const createTime = new Date(ticket.CreatedDate).getTime();
        const firstTouchTime = new Date(ticket.First_touch__c).getTime();
        const timeDiff = firstTouchTime - createTime;
        totalTime += timeDiff;
        count += 1;
      }
    });

    if (count === 0) {
      return null;
    }

    return totalTime / count;
  }
}
